import React from 'react';
import type { FormikProps } from 'formik/dist/types';
import type { FormValues } from 'src/components/AuthNew/components/RegistrationForm/types';
import { FormFields } from 'src/components/AuthNew/components/RegistrationForm/types';
import styled, { css } from 'styled-components';
import { Input } from 'src/components/AuthNew/components/Input';
import type { SetSharedState, SharedState } from 'src/components/AuthNew/types';
import { useTranslation } from '@lt/localization';
import { registrationErrorMap } from '../../helpers';
import { PhoneInput } from '../../../LoginForm/components';

type Props = {
  isMobile?: boolean;
  disablePhone?: boolean;
  disableEmail?: boolean;
  sharedState?: SharedState;
  setSharedState?: SetSharedState;
  backToLoginForm?: (value?: string) => void;
  errorCodes?: string;
  isStyleAsMobile?: boolean;
} & FormikProps<FormValues>;

export const Content = ({
  isMobile,
  disablePhone,
  disableEmail,
  backToLoginForm,
  setSharedState,
  sharedState,
  errorCodes,
  isStyleAsMobile,
}: Props) => {
  const { t } = useTranslation();

  const renderAppendAddon = (val: string) => {
    if (errorCodes && Object.keys(registrationErrorMap).includes(errorCodes))
      return (
        <AppendAddon
          onClick={() => backToLoginForm && backToLoginForm(val)}
          onKeyDown={() => backToLoginForm && backToLoginForm(val)}
        >
          {t('checkout.passwordForm.login')}
        </AppendAddon>
      );
    return null;
  };

  return (
    <>
      <Row isMobile={isMobile || isStyleAsMobile}>
        <StyledInput
          name={FormFields.Name}
          header={t('checkout.registrationForm.name.header')}
          placeholder={t('checkout.registrationForm.name.placeholder')}
          showSuccessIcon={false}
        />
        <StyledInput
          name={FormFields.Surname}
          header={t('checkout.registrationForm.surname.header')}
          placeholder={t('checkout.registrationForm.surname.placeholder')}
          showSuccessIcon={false}
        />
      </Row>
      <Row isMobile={isMobile || isStyleAsMobile}>
        <StyledInput
          name={FormFields.Email}
          header={t('checkout.registrationForm.email.header')}
          placeholder={t('checkout.registrationForm.email.placeholder')}
          showSuccessIcon={false}
          nativeInputProps={{
            disabled: disableEmail,
          }}
          type="email"
          renderAppendAddon={renderAppendAddon}
          hint={t('checkout.registrationForm.email.hint')}
        />

        <StyledPhoneInput
          setSharedState={setSharedState}
          sharedState={sharedState}
          name={FormFields.Phone}
          inputProps={{
            disabled: disablePhone,
            type: 'tel',
          }}
          renderAppendAddon={renderAppendAddon}
          hint={t('checkout.registrationForm.phone.hint')}
        />
      </Row>
    </>
  );
};

const Row = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: start;

  :not(:first-child) {
    margin-top: 8px;
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      flex-direction: column;

      ${StyledInput} {
        width: 100%;
        margin-bottom: 5px;
        :not(:first-child) {
          margin-left: 0;
        }
      }

      ${StyledPhoneInput} {
        width: 100%;
        margin-bottom: 5px;
        margin-left: 0;
      }
    `}
`;

const StyledInput = styled(Input)`
  flex: 1;

  :not(:first-child) {
    margin-left: 16px;
  }
`;

const StyledPhoneInput = styled(PhoneInput)`
  flex: 1;
  margin-left: 16px;
`;

const AppendAddon = styled.span`
  color: #0000ee;
  font-weight: bold;
  cursor: pointer;
  display: inline-block;
  position: relative;
  &:after {
    display: block;
    content: '';
    background-color: #0000ee;
    height: 2px;
    width: 0%;
    left: 50%;
    position: absolute;
    transition: width 0.2s ease-in-out;
    transform: translateX(-50%);
  }
  &:hover:after,
  &:focus:after {
    width: 100%;
  }
`;
